import type { LevelRun } from '../../core/level-runner';
import { t } from '../../i18n/es';
import { telemetryModel } from './telemetry-model';

/**
 * Route telemetry of the HUD (GDD §13.3, issue #59): a meter (role="meter", 0..1) with one mark per
 * checkpoint, the active one filled (shape, not only colour, GDD §13.6) and named "Checkpoint activo". The
 * fill moves in the 200 ms of the meters, 0 ms with reduced motion (--nv-motion-meter). It stands for
 * cr-art-ds-meter until the design system lands (#15). `update` touches the DOM only on changes.
 */
export function routeTelemetry(): { element: HTMLElement; update(run: LevelRun): void } {
  const element = document.createElement('div');
  element.className = 'telemetry';
  element.setAttribute('role', 'meter');
  element.setAttribute('aria-label', t('ui.telemetry.route'));
  element.setAttribute('aria-valuemin', '0');
  element.setAttribute('aria-valuemax', '1');
  // A meter must always carry its value (axe aria-required-attr); the first update replaces it (#140).
  element.setAttribute('aria-valuenow', '0');
  element.innerHTML = '<span class="telemetry-fill"></span><span class="telemetry-marks"></span>';
  const fill = element.querySelector<HTMLElement>('.telemetry-fill')!;
  const marks = element.querySelector<HTMLElement>('.telemetry-marks')!;
  let value = -1;
  let marksKey = '';

  return {
    element,
    update(run) {
      const model = telemetryModel(run);
      if (model.value !== value) {
        value = model.value;
        element.setAttribute('aria-valuenow', String(value));
        fill.style.transform = `scaleX(${value})`;
      }
      const key = model.marks.map((mark) => `${mark.id}:${mark.state}`).join(',');
      if (key !== marksKey) {
        marksKey = key;
        marks.innerHTML = model.marks
          .map(
            (mark) =>
              `<span class="telemetry-mark" data-mark="${mark.id}" data-state="${mark.state}" style="left: ${(mark.at * 100).toFixed(2)}%">${
                mark.state === 'active' ? `<span class="visually-hidden">${t('ui.telemetry.active')}</span>` : ''
              }</span>`,
          )
          .join('');
      }
    },
  };
}
